import { useEffect } from 'react';
import { Link, useLocation } from '@/lib/router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/cn';

type PublicPageId = 'home' | 'docs' | 'privacy' | 'terms';

const publicNavLinks = [
  { id: 'docs', label: 'Docs', to: '/docs' },
  { id: 'privacy', label: 'Privacy', to: '/privacy' },
  { id: 'terms', label: 'Terms', to: '/terms' },
] as const;

const interactiveLink =
  'inline-flex min-h-10 items-center rounded-md px-2 font-ui text-sm text-text-secondary no-underline transition-colors hover:bg-surface-1 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500';

export function PublicPageHeader({ active }: { active: PublicPageId }) {
  const { user, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nextTheme = theme === 'light' ? 'dark' : 'light';

  return (
    <header className="border-b border-border bg-surface-white px-6 py-3 max-mobile:px-4">
      <div className="mx-auto flex min-h-10 w-full max-w-[960px] min-w-0 flex-wrap items-center justify-between gap-x-5 gap-y-2">
        <Link
          to="/"
          aria-current={active === 'home' ? 'page' : undefined}
          className="inline-flex min-h-10 min-w-0 items-center no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
        >
          <span className="min-w-0 font-chrome text-sm font-semibold uppercase leading-none tracking-[0.05em] [overflow-wrap:anywhere]">
            <span className="text-navy-500">Prometheus</span>{' '}
            <span className="text-text-tertiary max-mobile:hidden">
              Legal Intelligence
            </span>
          </span>
        </Link>

        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1 max-mobile:ml-0 max-mobile:w-full max-mobile:justify-between">
          <nav
            aria-label="Primary"
            className="flex min-w-0 flex-wrap items-center gap-1"
          >
            {publicNavLinks.map((link) => (
              <Link
                key={link.id}
                to={link.to}
                aria-current={active === link.id ? 'page' : undefined}
                className={cn(
                  interactiveLink,
                  active === link.id && 'font-semibold text-text-primary',
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <button
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${nextTheme} theme`}
            className="inline-flex min-h-10 items-center rounded-md border border-border bg-surface-0 px-3 font-ui text-sm text-text-secondary transition-colors hover:border-border-emphasis hover:bg-surface-1 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
          >
            {nextTheme === 'dark' ? 'Dark' : 'Light'}
          </button>

          <div
            className="flex min-h-10 min-w-[5rem] items-center justify-end"
            aria-live="polite"
          >
            {loading ? (
              <span
                role="status"
                className="font-ui text-xs text-text-tertiary"
              >
                Checking session…
              </span>
            ) : (
              <Link
                to={user ? '/account' : '/auth?mode=signin'}
                className={interactiveLink}
              >
                {user ? 'Account' : 'Sign in'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export function PublicPageFooter({
  active,
  width = 'wide',
}: {
  active: PublicPageId;
  width?: 'wide' | 'reading';
}) {
  return (
    <footer className="border-t border-border bg-surface-1 px-6 py-8 max-mobile:px-4">
      <div
        className={cn(
          'mx-auto flex min-w-0 flex-wrap items-center justify-between gap-4 font-ui text-xs text-text-tertiary',
          width === 'reading' ? 'max-w-[860px]' : 'max-w-[960px]',
        )}
      >
        <div className="[overflow-wrap:anywhere]">
          © 2026 Prometheus Legal Intelligence
        </div>
        <nav
          aria-label="Footer"
          className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1"
        >
          <Link
            to="/"
            aria-current={active === 'home' ? 'page' : undefined}
            className={interactiveLink}
          >
            Home
          </Link>
          <Link
            to="/docs"
            aria-current={active === 'docs' ? 'page' : undefined}
            className={interactiveLink}
          >
            Docs
          </Link>
          <Link
            to="/privacy"
            aria-current={active === 'privacy' ? 'page' : undefined}
            className={interactiveLink}
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            aria-current={active === 'terms' ? 'page' : undefined}
            className={interactiveLink}
          >
            Terms
          </Link>
          <a
            href="mailto:adrian@prometheuslaw.app"
            className={cn(interactiveLink, '[overflow-wrap:anywhere]')}
          >
            Contact
          </a>
        </nav>
      </div>
    </footer>
  );
}

export function SkipToMainContent() {
  useStableHashTarget();

  return (
    <a
      href="#main-content"
      onClick={() => document.getElementById('main-content')?.focus()}
      className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:inline-flex focus:min-h-10 focus:items-center focus:rounded-md focus:bg-text-primary focus:px-4 focus:font-ui focus:text-sm focus:font-semibold focus:text-surface-0 focus:no-underline focus:outline-2 focus:outline-offset-2 focus:outline-navy-500"
    >
      Skip to main content
    </a>
  );
}

/**
 * Browser fragment navigation happens before a client-rendered route has
 * mounted its headings. Reconcile the hash after React commits so direct
 * public/legal deep links scroll to, and name, the intended reading position.
 */
function useStableHashTarget() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    let targetId: string;
    try {
      targetId = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }
    if (!targetId) return;

    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (!(target instanceof HTMLElement)) return;

      target.scrollIntoView({ block: 'start' });
      target.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [hash]);
}
